<script setup>
/**
 * Apps.
 *
 * Archetype F, and the closest thing settings has to a shop window: every
 * integration Tola knows about, each card saying whether it is switched on.
 * Search moves into the header's action slot so the grid starts at the top of
 * the body rather than a third of the way down it.
 */
import { computed, onMounted, ref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useBranding } from 'shared/composables/useBranding';
import { picoSearch } from '@scmmishra/pico-search';

import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import SettingsCardGrid from 'dashboard/components-next/Settings/SettingsCardGrid.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import SettingsSearchInput from 'dashboard/components-next/Settings/SettingsSearchInput.vue';
import IntegrationItem from './IntegrationItem.vue';

const store = useStore();
const { replaceInstallationName } = useBranding();

const searchQuery = ref('');

const uiFlags = useMapGetter('integrations/getUIFlags');
const integrationList = useMapGetter('integrations/getAppIntegrations');

const filteredIntegrationList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return integrationList.value;
  return picoSearch(integrationList.value, query, ['name', 'description']);
});

onMounted(() => {
  store.dispatch('integrations/get');
});
</script>

<template>
  <SettingsPageShell
    wide
    :title="$t('SIDEBAR.APPS')"
    :description="
      replaceInstallationName($t('INTEGRATION_SETTINGS.DESCRIPTION'))
    "
  >
    <template #tabs>
      <SettingsSearchInput
        v-model="searchQuery"
        :placeholder="$t('INTEGRATION_SETTINGS.SEARCH_PLACEHOLDER')"
      />
    </template>

    <SettingsSection
      v-if="uiFlags.isFetching"
      role="status"
      aria-busy="true"
      :aria-label="$t('INTEGRATION_SETTINGS.LOADING')"
    >
      <div
        v-for="row in 4"
        :key="row"
        class="flex items-center gap-3 px-4 py-4"
      >
        <Skeleton variant="circle" :announce="false" class="size-8" />
        <Skeleton variant="text" :lines="2" :announce="false" class="flex-1" />
      </div>
    </SettingsSection>

    <p
      v-else-if="!filteredIntegrationList.length && searchQuery"
      class="py-20 text-base text-center text-n-slate-11"
    >
      {{ $t('INTEGRATION_SETTINGS.NO_RESULTS') }}
    </p>

    <SettingsCardGrid v-else>
      <IntegrationItem
        v-for="item in filteredIntegrationList"
        :id="item.id"
        :key="item.id"
        :logo="item.logo"
        :name="item.name"
        :description="item.description"
        :enabled="item.enabled"
      />
    </SettingsCardGrid>
  </SettingsPageShell>
</template>
